import { RequestStatus, defaults, entities, getAPIMethod } from 'typed/api';
import { ExtractAction, NetworkAction, action, entityActionSet, networkAction } from './utils';

type AddDialogScheduleSettings = NetworkAction<
'ADD_DIALOG_SCHEDULE_SETTING',
{entity: entities.DialogScheduleSettingsReport; sync?: boolean; reloadAfterSync?: boolean; createAndSendBatch?: boolean; useSystemTemplates?: boolean; },
number
>;

interface DialogScheduleFlowArguments {
    parentID: number;
    entities: entities.DialogScheduleFlow[];
    sync?: boolean;
    reloadAfterSync?: boolean;
    useSystemTemplates?: boolean;
}

type AddAllDialogScheduleFlows = NetworkAction<'ADD_ALL_DIALOG_SCHEDULE_FLOWS', DialogScheduleFlowArguments, number[]>;
type EditAllDialogScheduleFlows = NetworkAction<'EDIT_ALL_DIALOG_SCHEDULE_FLOWS', DialogScheduleFlowArguments, void>;

interface AddDialogFlowMessageOptions { parentMessageID?: number | null; parentOptionID?: number | null; }
type AddDialogFlowMessage = NetworkAction<
'ADD_DIALOG_FLOW_TEMPLATE_MESSAGE',
{ parentID: number; entity: entities.DialogFlowTemplateMessageReport; sync?: boolean; reloadAfterSync?: boolean; options?: AddDialogFlowMessageOptions; },
number
>;

export const
    dialogScheduleSettings = {
        ...entityActionSet(defaults.DialogScheduleSettingsReport, {
            loadAll: networkAction('LOAD_ALL_DIALOG_SCHEDULE_SETTINGS', getAPIMethod('DialogScheduleSettings', 'loadAll')),
            load: networkAction('LOAD_DIALOG_SCHEDULE_SETTING', getAPIMethod('DialogScheduleSettings', 'load')),
            edit: networkAction('EDIT_DIALOG_SCHEDULE_SETTING', getAPIMethod('DialogScheduleSettings', 'edit')),
            delete: networkAction('DELETE_DIALOG_SCHEDULE_SETTING', getAPIMethod('DialogScheduleSettings', 'delete')),
            voidAll: action('VOID_ALL_DIALOG_SCHEDULE_SETTINGS')
        }),
        add: (entity: entities.DialogScheduleSettingsReport, sync = false, reloadAfterSync = false, createAndSendBatch = false, useSystemTemplates = false): AddDialogScheduleSettings =>
            async(dispatch: any) => {
                dispatch({
                    type: 'ADD_DIALOG_SCHEDULE_SETTING',
                    payload: { entity, entityID: entity.ID, status: RequestStatus.UNSYNCED, createAndSendBatch }
                });

                if (!sync) { return entity.ID; }

                const entityID = await dispatch(networkAction(
                    'ADD_DIALOG_SCHEDULE_SETTING',
                    { entity, entityID: entity.ID, createAndSendBatch, useSystemTemplates },
                    getAPIMethod<number>('DialogScheduleSettings', 'add'),
                    entity,
                    { createAndSendBatch, useSystemTemplates }
                ));

                if (!reloadAfterSync) { return entityID; }

                return await dispatch(networkAction(
                    'LOAD_DIALOG_SCHEDULE_SETTING',
                    { entityID },
                    getAPIMethod<entities.DialogScheduleSettingsReport>('DialogScheduleSettings', 'load'),
                    entityID
                ));
            }
    },

    { loadAll: loadAllDialogScheduleRhytms } = entityActionSet(defaults.DialogScheduleRhytm, {
        loadAll: networkAction('LOAD_ALL_DIALOG_SCHEDULE_RHYTMS', getAPIMethod('DialogScheduleRhytm', 'loadAll'))
    }),

    { loadAll: loadAllDialogDistributionChannels } = entityActionSet(defaults.DialogDistributionChannel, {
        loadAll: networkAction('LOAD_ALL_DIALOG_DISTRIBUTION_CHANNELS', getAPIMethod('DialogDistributionChannel', 'loadAll'))
    }),

    dialogScheduleFlows = {
        loadMany: (parentID: number, entityIDs: number[]) =>
            networkAction('LOAD_MANY_DIALOG_SCHEDULE_FLOWS', { parentID, entityIDs }, getAPIMethod<entities.DialogScheduleFlow[]>('DialogScheduleFlow', 'loadMany'), entityIDs),
        addAll: (parentID: number, entities: entities.DialogScheduleFlow[], sync = false, reloadAfterSync = false, useSystemTemplates = false): AddAllDialogScheduleFlows =>
            async(dispatch: any) => {
                const basePayload = { parentID, entityIDs: entities.map(({ ID }) => ID), entities, useSystemTemplates };
                dispatch({ type: 'ADD_ALL_DIALOG_SCHEDULE_FLOWS', payload: { ...basePayload, status: RequestStatus.UNSYNCED } });

                if (!sync) {
                    return entities.map(({ ID }) => ID);
                }
                else {
                    const action = networkAction(
                        'ADD_ALL_DIALOG_SCHEDULE_FLOWS',
                        basePayload,
                        getAPIMethod<number[]>('DialogScheduleFlow', 'addAll'),
                        entities,
                        { useSystemTemplates }
                    );

                    if (!reloadAfterSync) {
                        return await dispatch(action);
                    }
                    else {
                        const response = await dispatch(action);
                        return dispatch(dialogScheduleFlows.loadMany(parentID, response));
                    }
                }
            },
        editAll: (parentID: number, entities: entities.DialogScheduleFlow[], sync = false, reloadAfterSync = false, useSystemTemplates = false): EditAllDialogScheduleFlows =>
            async(dispatch: any) => {
                const basePayload = { parentID, entityIDs: entities.map(({ ID }) => ID), entities, useSystemTemplates };
                dispatch({ type: 'EDIT_ALL_DIALOG_SCHEDULE_FLOWS', payload: { ...basePayload, status: RequestStatus.UNSYNCED } });

                if (!sync) {
                    return undefined;
                }
                else {
                    const action = networkAction(
                        'EDIT_ALL_DIALOG_SCHEDULE_FLOWS',
                        basePayload,
                        getAPIMethod('DialogScheduleFlow', 'editAll'),
                        entities,
                        { useSystemTemplates }
                    );

                    if (!reloadAfterSync) {
                        return await dispatch(action);
                    }
                    else {
                        await dispatch(action);
                        return dispatch(dialogScheduleFlows.loadMany(parentID, basePayload.entityIDs));
                    }
                }
            },
        ...entityActionSet(defaults.DialogScheduleSettingsReport, defaults.DialogScheduleFlow, {
            deleteAll: networkAction('DELETE_ALL_DIALOG_SCHEDULE_FLOWS', getAPIMethod('DialogScheduleFlow', 'deleteAll'))
        })
    },

    dialogScheduleUsers = entityActionSet(defaults.DialogScheduleSettingsReport, defaults.DialogScheduleUser, {
        addAll: networkAction('ADD_ALL_DIALOG_SCHEDULE_USERS', getAPIMethod('DialogScheduleUser', 'addAll')),
        deleteAll: networkAction('DELETE_ALL_DIALOG_SCHEDULE_USERS', getAPIMethod('DialogScheduleUser', 'deleteAll'))
    }),

    dialogScheduleBatches = {
        ...entityActionSet(defaults.DialogScheduleSettingsReport, defaults.DialogScheduleBatchReport, {
            load: networkAction('LOAD_DIALOG_SCHEDULE_BATCH', getAPIMethod('DialogScheduleBatch', 'load')),
            add: networkAction('ADD_DIALOG_SCHEDULE_BATCH', getAPIMethod('DialogScheduleBatch', 'add')),
            edit: networkAction('EDIT_DIALOG_SCHEDULE_BATCH', getAPIMethod('DialogScheduleBatch', 'edit')),
            delete: networkAction('DELETE_DIALOG_SCHEDULE_BATCH', getAPIMethod('DialogScheduleBatch', 'delete'))
        }),
        sendBatch: (parentID: number, entityID: number) =>
            networkAction('SEND_DIALOG_SCHEDULE_BATCH', { parentID, entityID }, getAPIMethod('DialogScheduleBatch', 'sendBatch'), entityID),
        sendBatchToTeams: (parentID: number, entityID: number, conversationID: number | null) =>
            networkAction(
                'SEND_DIALOG_SCHEDULE_BATCH_TO_MICROSOFT_TEAMS',
                { parentID, entityID, conversationID },
                getAPIMethod('DialogScheduleBatch', 'sendBatchToTeams'),
                entityID,
                { conversationID }
            ),
        sendBatchToWhatsApp: (parentID: number, entityID: number, phoneNumber: string) =>
            networkAction(
                'SEND_DIALOG_SCHEDULE_BATCH_TO_WHATSAPP',
                { parentID, entityID, phoneNumber },
                getAPIMethod('DialogScheduleBatch', 'sendBatchToWhatsApp'),
                entityID,
                phoneNumber
            ),
        createResultWorkshop: (parentID: number, entityID: number, closeBatch: boolean) =>
            networkAction(
                'CREATE_DIALOG_SCHEDULE_BATCH_RESULT_WORKSHOP',
                { parentID, entityID, closeBatch },
                getAPIMethod<number | null>('DialogScheduleBatch', 'createBatchResultWorkshop'),
                entityID,
                closeBatch
            ),
        createResultStrategy: (parentID: number, entityID: number) =>
            networkAction(
                'CREATE_DIALOG_SCHEDULE_BATCH_RESULT_STRATEGY',
                { parentID, entityID },
                getAPIMethod<number | null>('DialogScheduleBatch', 'createBatchResultStrategy'),
                entityID
            )
    },

    dialogFlowTemplates = {
        ...entityActionSet(defaults.DialogFlowTemplateReport, {
            loadAll: networkAction('LOAD_ALL_DIALOG_FLOW_TEMPLATES', getAPIMethod('DialogFlowTemplate', 'loadAll')),
            add: networkAction('ADD_DIALOG_FLOW_TEMPLATE', getAPIMethod('DialogFlowTemplate', 'add')),
            edit: networkAction('EDIT_DIALOG_FLOW_TEMPLATE', getAPIMethod('DialogFlowTemplate', 'edit')),
            delete: networkAction('DELETE_DIALOG_FLOW_TEMPLATE', getAPIMethod('DialogFlowTemplate', 'delete'))
        }),
        storeScreenshot: (entityID: number, image: entities.FileTransferHelperEntity) =>
            networkAction(
                'STORE_DIALOG_FLOW_TEMPLATE_SCREENSHOT',
                { entityID, image },
                getAPIMethod<string>('DialogFlowTemplate', 'storeDialogFlowTemplateScreenshot'),
                image,
                entityID
            )
    },

    { loadAll: loadAllDialogFlowSystemTemplates } = entityActionSet(defaults.DialogFlowTemplateReport, {
        loadAll: networkAction('LOAD_ALL_DIALOG_FLOW_SYSTEM_TEMPLATES', getAPIMethod('DialogFlowTemplate', 'loadAllSystemTemplates'))
    }),

    dialogFlowTemplateMessages = {
        ...entityActionSet(defaults.DialogFlowTemplateReport, defaults.DialogFlowTemplateMessageReport, {
            load: networkAction('LOAD_DIALOG_FLOW_TEMPLATE_MESSAGE', getAPIMethod('DialogFlowTemplateMessage', 'load')),
            editAll: networkAction('EDIT_ALL_DIALOG_FLOW_TEMPLATE_MESSAGES', getAPIMethod('DialogFlowTemplateMessage', 'editAll')),
            edit: networkAction('EDIT_DIALOG_FLOW_TEMPLATE_MESSAGE', getAPIMethod('DialogFlowTemplateMessage', 'edit')),
            delete: networkAction('DELETE_DIALOG_FLOW_TEMPLATE_MESSAGE', getAPIMethod('DialogFlowTemplateMessage', 'delete'))
        }),
        add: (parentID: number, entity: entities.DialogFlowTemplateMessageReport, sync = false, reloadAfterSync = false, options: AddDialogFlowMessageOptions = {}): AddDialogFlowMessage =>
            async(dispatch: any) => {
                dispatch({
                    type: 'ADD_DIALOG_FLOW_TEMPLATE_MESSAGE',
                    payload: { parentID, entity, entityID: entity.ID, status: RequestStatus.UNSYNCED, ...options }
                });

                if (!sync) { return entity.ID; }

                const entityID = await dispatch(networkAction(
                    'ADD_DIALOG_FLOW_TEMPLATE_MESSAGE',
                    { parentID, entity, entityID: entity.ID, ...options },
                    getAPIMethod<number>('DialogFlowTemplateMessage', 'add'),
                    entity,
                    options
                ));

                if (!reloadAfterSync) { return entityID; }

                return await dispatch(networkAction(
                    'LOAD_DIALOG_FLOW_TEMPLATE_MESSAGE',
                    { parentID, entityID },
                    getAPIMethod<entities.DialogFlowTemplateMessageReport>('DialogFlowTemplateMessage', 'load'),
                    entityID
                ));
            }
    },

    { loadAll: loadAllDialogFlowMessageTypes } = entityActionSet(defaults.DialogFlowTemplateMessageType, {
        loadAll: networkAction('LOAD_ALL_DIALOG_FLOW_MESSAGE_TYPES', getAPIMethod('DialogFlowTemplateMessageType', 'loadAll'))
    }),

    { loadAll: loadAllDialogFlowMessageConversionRules } = entityActionSet(defaults.DialogFlowTemplateMessageConversionRule, {
        loadAll: networkAction('LOAD_ALL_DIALOG_FLOW_MESSAGE_CONVERSION_RULES', getAPIMethod('DialogFlowTemplateMessageConversionRule', 'loadAll'))
    }),

    { loadAll: loadAllDialogFlowLogicGateValueSources } = entityActionSet(defaults.DialogFlowLogicGateValueSource, {
        loadAll: networkAction('LOAD_ALL_DIALOG_FLOW_LOGIC_GATE_VALUE_SOURCES', getAPIMethod('DialogFlowLogicGateValueSource', 'loadAll'))
    }),

    { loadAll: loadAllDialogFlowLogicGateMatchRules } = entityActionSet(defaults.DialogFlowLogicGateMatchRule, {
        loadAll: networkAction('LOAD_ALL_DIALOG_FLOW_LOGIC_GATE_MATCH_RULES', getAPIMethod('DialogFlowLogicGateMatchRule', 'loadAll'))
    }),

    dialogFlowMultiOptionDataSources = {
        ...entityActionSet(defaults.DialogFlowMultiOptionDataSource, {
            loadAll: networkAction('LOAD_ALL_DIALOG_FLOW_MULTI_OPTION_DATA_SOURCES', getAPIMethod('DialogFlowMultiOptionDataSource', 'loadAll'))
        }),
        loadOptions: (entityID: number) =>
            networkAction('LOAD_DIALOG_FLOW_MULTI_OPTION_DATA_SOURCE_OPTIONS', { entityID }, getAPIMethod('DialogFlowMultiOptionDataSource', 'loadDataSourceOptionsJson'), entityID)
    },

    dialogFlowTemplateMessageOptions = entityActionSet(defaults.DialogFlowTemplateReport, defaults.DialogFlowTemplateMessageReport, defaults.DialogFlowTemplateMessageOption, {
        addAll: networkAction('ADD_ALL_DIALOG_FLOW_TEMPLATE_MESSAGE_OPTIONS', getAPIMethod('DialogFlowTemplateMessageOption', 'addAll')),
        add: networkAction('ADD_DIALOG_FLOW_TEMPLATE_MESSAGE_OPTION', getAPIMethod('DialogFlowTemplateMessageOption', 'add')),
        editAll: networkAction('EDIT_ALL_DIALOG_FLOW_TEMPLATE_MESSAGE_OPTIONS', getAPIMethod('DialogFlowTemplateMessageOption', 'editAll')),
        edit: networkAction('EDIT_DIALOG_FLOW_TEMPLATE_MESSAGE_OPTION', getAPIMethod('DialogFlowTemplateMessageOption', 'edit')),
        deleteAll: networkAction('DELETE_ALL_DIALOG_FLOW_TEMPLATE_MESSAGE_OPTIONS', getAPIMethod('DialogFlowTemplateMessageOption', 'deleteAll')),
        delete: networkAction('DELETE_DIALOG_FLOW_TEMPLATE_MESSAGE_OPTION', getAPIMethod('DialogFlowTemplateMessageOption', 'delete'))
    }),

    dialogFlowSessions = {
        ...entityActionSet(defaults.DialogFlowSession, { load: networkAction('LOAD_DIALOG_FLOW_SESSION', getAPIMethod('DialogFlowSession', 'load')) }),
        createPreviewSession: (templateID: number, useSystemTemplate = false) =>
            networkAction(
                'CREATE_PREVIEW_DIALOG_FLOW_SESSION',
                { templateID, useSystemTemplate },
                getAPIMethod<entities.DialogFlowSession>('DialogFlowSession', 'createPreviewSession'),
                templateID,
                useSystemTemplate
            )
    };

export type DialogAction = ExtractAction<
| typeof dialogScheduleSettings
| typeof loadAllDialogScheduleRhytms
| typeof loadAllDialogDistributionChannels
| typeof dialogScheduleFlows
| typeof dialogScheduleUsers
| typeof dialogScheduleBatches
| typeof dialogFlowTemplates
| typeof loadAllDialogFlowSystemTemplates
| typeof dialogFlowTemplateMessages
| typeof loadAllDialogFlowMessageTypes
| typeof loadAllDialogFlowMessageConversionRules
| typeof loadAllDialogFlowLogicGateValueSources
| typeof loadAllDialogFlowLogicGateMatchRules
| typeof dialogFlowMultiOptionDataSources
| typeof dialogFlowTemplateMessageOptions
| typeof dialogFlowSessions
>;
